import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Text,
  Stack,
  Button,
  Flex,
  Divider,
  Alert,
  AlertIcon,
  Badge,
  Heading,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { FaStar, FaRegStar, FaStarHalfAlt } from 'react-icons/fa';
import ReactApexChart from 'react-apexcharts';
import { react_api } from '../../../../config';

const Evaluate = ({ 
  visualization, 
  selectedGoal, 
  username, 
  bearerToken, 
  themeColors 
}) => {
  // Local states for evaluation
  const [evaluationData, setEvaluationData] = useState(null);
  const [isEvaluationLoading, setIsEvaluationLoading] = useState(false);
  const [evaluationError, setEvaluationError] = useState(null);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const toast = useToast();
  
  // Effect to automatically evaluate the visualization when the component is loaded or when dependencies change
  useEffect(() => {
    if (visualization && selectedGoal && visualization.length > 0 && visualization[0].code) {
      evaluateVisualization();
    }
  }, [visualization, selectedGoal]);
  
  // Function to evaluate the visualization
  const evaluateVisualization = async () => {
    if (!visualization || !selectedGoal || !visualization[0].code) return;
    
    setIsEvaluationLoading(true);
    setEvaluationError(null);
    
    try {
      // Make API call to evaluate the visualization
      const response = await axios.post(`${react_api}/externalService/novaServices`, {
        username: username,
        BearerToken: bearerToken,
        service: 6,
        request: {
          code: visualization[0].code,
          goal: selectedGoal,
          library: "seaborn"
        }
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Check the response format and save the evaluations
      if (response.data && response.data.request && response.data.request.evaluations) {
        setEvaluationData(response.data.request);
        // Select the first dimension as default
        if (response.data.request.evaluations.length > 0) {
          setSelectedDimension(response.data.request.evaluations[0]);
        }
      } else {
        throw new Error('Invalid response format from evaluation API');
      }
      
    } catch (err) {
      console.error('Error evaluating visualization:', err);
      
      let errorMessage = 'Failed to evaluate visualization. Please try again.';
      
      if (err.response?.status === 422) {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail) && errorDetail.length > 0) {
          errorMessage = errorDetail[0]?.msg || 'Validation error';
        }
      } else {
        errorMessage = err.response?.data?.message || err.message || 'Failed to evaluate visualization';
      }
      
      setEvaluationError(errorMessage);
    } finally {
      setIsEvaluationLoading(false);
    }
  };
  
  // Function to render stars based on the score
  const renderStars = (score) => {
    const fullStars = Math.floor(score / 2);
    const halfStar = score % 2 >= 1;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
    
    return (
      <Flex>
        {[...Array(fullStars)].map((_, i) => (
          <FaStar key={`full-${i}`} color="#FFD700" />
        ))}
        {halfStar && <FaStarHalfAlt color="#FFD700" />}
        {[...Array(emptyStars)].map((_, i) => (
          <FaRegStar key={`empty-${i}`} color="#FFD700" />
        ))}
      </Flex>
    );
  };
  
  // Function to configure the radar chart
  const getRadarChartOptions = () => {
    if (!evaluationData || !evaluationData.evaluations) return null;
    
    const dimensions = evaluationData.evaluations.map(e => e.dimension);
    const scores = evaluationData.evaluations.map(e => e.score);
    
    return {
      series: [{
        name: 'Score',
        data: scores
      }],
      options: {
        chart: {
          height: 350,
          type: 'radar',
          toolbar: {
            show: false
          },
          dropShadow: {
            enabled: true,
            blur: 1,
            left: 1,
            top: 1
          }
        },
        colors: [themeColors?.titleIconColor || '#9acd32'],
        stroke: {
          width: 2
        },
        fill: {
          opacity: 0.2
        },
        markers: {
          size: 5,
          hover: {
            size: 8
          }
        },
        title: {
          text: 'Visualization Evaluation'
        },
        xaxis: {
          categories: dimensions,
          labels: {
            show: true,
            style: {
              colors: Array(dimensions.length).fill(themeColors?.fontColor || '#0b4e5aff'),
              fontSize: '14px'
            }
          }
        },
        yaxis: {
          show: false,
          min: 0,
          max: 10,
          tickAmount: 5
        },
        plotOptions: {
          radar: {
            polygons: {
              strokeColors: '#e8e8e8',
              fill: {
                colors: ['#f8f8f8', '#fff']
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + '/10';
            }
          }
        }
      }
    };
  };
  
  // Component for the evaluation section
  return (
    <Box mt={6}>
      {!evaluationData && !isEvaluationLoading && !evaluationError && (
        <Box
          borderWidth="1px"
          borderRadius="md"
          p={4}
          bg={themeColors?.servicesBg || '#f6f3ec'}
          borderColor={themeColors?.borderColor || '#178da1ff'}
          boxShadow="sm"
        >
          <Flex alignItems="center" justifyContent="space-between">
            <Flex alignItems="center">
              <Text fontSize="md" fontWeight="bold" color={themeColors?.fontColor || '#0b4e5aff'}>
                Evaluate Visualization Quality
              </Text>
              <Text ml={2} fontSize="sm" color="gray.600">
                Get an AI assessment of your data visualization
              </Text>
            </Flex>
            <Button
              onClick={evaluateVisualization}
              size="sm"
              bg={themeColors?.buttonColor || '#083c46ff'}
              color="white"
              _hover={{ bg: '#0a4956' }}
            >
              Evaluate
            </Button>
          </Flex>
        </Box>
      )}

      {/* Show a loader during evaluation loading */}
      {isEvaluationLoading && (
        <Box textAlign="center" p={4}>
          <Spinner size="lg" color={themeColors?.titleIconColor || '#9acd32'} />
          <Text mt={2} color={themeColors?.fontColor || '#0b4e5aff'}>
            Analyzing and evaluating your visualization...
          </Text>
        </Box>
      )}

      {/* Show an error message if evaluation fails */}
      {evaluationError && (
        <Box mt={4}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text>{evaluationError}</Text>
          </Alert>
          <Button
            mt={2}
            onClick={evaluateVisualization}
            size="sm"
            bg={themeColors?.buttonColor || '#083c46ff'}
            color="white"
            _hover={{ bg: '#0a4956' }}
          >
            Try Again
          </Button>
        </Box>
      )}

      {/* Show evaluations when available */}
      {evaluationData && (
        <Box mt={4}>
          <Text fontSize="sm" color="gray.600" mb={4}>
            The visualization has been evaluated across multiple dimensions. Select a dimension to view rationale for the score
          </Text>
          
          <Flex direction={["column", "column", "row"]} spacing={6} gap={6}>
            {/* Radar chart with scores across various dimensions - LEFT SIDE */}
            <Box
              flex="1"
              borderWidth="1px"
              borderRadius="md"
              borderColor="gray.200"
              bg="white"
              overflow="hidden"
              p={4}
            >
              {getRadarChartOptions() && (
                <ReactApexChart
                  options={getRadarChartOptions().options}
                  series={getRadarChartOptions().series}
                  type="radar"
                  height="90%"
                />
              )}
            </Box>
            
            {/* List of dimensions with stars and progress bars */}
            <Box flex="1">
              <Stack spacing={3}>
                {evaluationData.evaluations.map((dimension) => (
                  <Box 
                    key={dimension.dimension}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    cursor="pointer"
                    bg="white"
                    borderColor={selectedDimension?.dimension === dimension.dimension ? themeColors.borderColor || '#178da1ff' : "gray.200"}
                    _hover={{ borderColor: themeColors.borderColor || '#178da1ff', boxShadow: "sm" }}
                    onClick={() => setSelectedDimension(dimension)}
                  >
                    <Flex justify="space-between" align="center">
                      <Flex align="center">
                        {renderStars(dimension.score)}
                      </Flex>
                      <Text fontWeight="medium" ml={3}>
                        {dimension.dimension}
                      </Text>
                    </Flex>
                    
                    <Box mt={2} w="100%">
                      <Box
                        h="8px"
                        w="100%"
                        bgColor="gray.200"
                        borderRadius="full"
                        overflow="hidden"
                      >
                        <Box
                          h="100%"
                          w={`${dimension.score * 10}%`}
                          bgColor={dimension.score >= 7 ? "green.400" : dimension.score >= 5 ? "yellow.400" : "red.400"}
                          borderRadius="full"
                        />
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
              
              {/* Selected dimension details */}
              {selectedDimension && (
                <Box
                  mt={4}
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="gray.50"
                  borderColor="gray.200"
                >
                  <Flex justify="space-between" align="center" mb={3}>
                    <Heading as="h4" size="md" color={themeColors?.fontColor || '#0b4e5aff'}>
                      {selectedDimension.dimension}
                    </Heading>
                    <Box fontSize="24px" fontWeight="bold" color="green.500">
                      {selectedDimension.score.toFixed(1)}
                      <Box as="span" fontSize="14px" color="gray.500" ml={1}>
                        out of 10
                      </Box>
                    </Box>
                  </Flex>
                  
                  <Box mt={2} p={1}>
                    <Text>{selectedDimension.rationale}</Text>
                  </Box>
                </Box>
              )}
            </Box>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default Evaluate;
